import React, { createContext, useContext, useState } from "react";

import { AuthorizationStatusEnum } from "../services/authorizationService/getAuthorization";

type Address = {
  zipCode: string;
  state: string;
  city: string;
  neighborhood: string;
  street: string;
};

type CustomerData = {
  id: string;
  name: string;
  email: string;
  phone: string;
  cpf: string;
  birthDate: string;
  address: Address;
};

type PreTransactionData = {
  id: string;
  status: AuthorizationStatusEnum;
  processoId: string;
  propostaId: string | null;
  autorizacaoEnviada: boolean;
};

type SelectedOffer = {
  produtoId: number;
  tabelaJurosId: number;
  diaRecebimentoId: number;
  vencimento: string;
  valorSolicitado: number;
  prazo: number;
  valorParcela: number;
  commission: number;
};

type CrefazCltContextType = {
  customerData: Partial<CustomerData>;
  setCustomerData: (data: Partial<CustomerData>) => void;
  clearCustomerData: () => void;
  preTransaction: Partial<PreTransactionData>;
  setPreTransaction: (data: Partial<PreTransactionData>) => void;
  clearPreTransaction: () => void;
  selectedOffer: SelectedOffer | null;
  setSelectedOffer: (offer: SelectedOffer | null) => void;
};

const CrefazCltContext = createContext<CrefazCltContextType | undefined>(
  undefined,
);

export const CrefazCltProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [customerData, setCustomerDataState] = useState<Partial<CustomerData>>(
    {},
  );
  const [preTransaction, setPreTransactionState] = useState<
    Partial<PreTransactionData>
  >({});
  const [selectedOffer, setSelectedOffer] = useState<SelectedOffer | null>(
    null,
  );

  const setCustomerData = (data: Partial<CustomerData>) => {
    setCustomerDataState((prev) => ({ ...prev, ...data }));
  };

  const clearCustomerData = () => setCustomerDataState({});

  const setPreTransaction = (data: Partial<PreTransactionData>) => {
    setPreTransactionState((prev) => ({ ...prev, ...data }));
  };

  const clearPreTransaction = () => setPreTransactionState({});

  return (
    <CrefazCltContext.Provider
      value={{
        customerData,
        setCustomerData,
        clearCustomerData,
        preTransaction,
        setPreTransaction,
        clearPreTransaction,
        selectedOffer,
        setSelectedOffer,
      }}
    >
      {children}
    </CrefazCltContext.Provider>
  );
};

export const useCrefazClt = (): CrefazCltContextType => {
  const context = useContext(CrefazCltContext);
  if (!context)
    throw new Error("useCrefazClt must be used within a CrefazCltProvider");
  return context;
};
